import { formatDistance } from 'date-fns'
import React, { ReactElement, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import styled from 'styled-components'
import Radom, { DetectedDeposit, DetectedPayment, DetectedPaymentSource, SenderAddress } from '../state/Radom'
import { formatExplorerUrl } from '../util/Chains'
import { RADOM_COLORS } from '../util/Constants'
import { getMethod } from '../util/Managed'
import { roundAssetAmount, shortAddress } from '../util/Util'
import { InlineNotice, InlinePill } from './DashboardPrimitives'
import DetailsDrawer, { DrawerCard, DrawerDetailLabel, DrawerDetailRow, DrawerDetailValue } from './DetailsDrawer'
import LinkExternal from './LinkExternal'
import PaymentMethodDisplay from './PaymentMethodDisplay'

// Inbound transactions the chain has shown us that are still waiting to be
// final: deposits to a deposit address and payments against a checkout,
// payment link or invoice. Nothing is credited until the wait ends and the
// transaction is still in the canonical chain, so these are listed with an
// "awaiting confirmations" status next to everything else.

const REFRESH_MS = 30_000

export type DetectedKind = 'deposit' | 'payment'

export interface DetectedRow {
  id: string
  kind: DetectedKind
  network: string
  token?: string
  transactionHash: string
  amount: number
  /** What the payment asked for; only on payments. */
  expectedAmount?: number
  blockNumber?: number
  /** Block time when known, otherwise when the payment was started. */
  seenAt: string
  confirmations: number
  requiredConfirmations: number
  awaitsFinalizedPass: boolean
  senderAddresses: SenderAddress[]
  /** What the money is for, as shown in the Related column. */
  related: { name: string, url: string, subtitle: string }
}

const sourceLabel = (source: DetectedPaymentSource): { name: string, url: string } => {
  switch (source) {
  case 'CheckoutSession': return { name: 'Checkout session', url: '/checkout_sessions' }
  case 'PaymentLinkOrder': return { name: 'Payment link', url: '/payment_links' }
  case 'DonationLinkOrder': return { name: 'Donation link', url: '/payment_links' }
  case 'Invoice': return { name: 'Invoice', url: '/invoices' }
  case 'Subscription': return { name: 'Subscription', url: '/subscriptions' }
  default: return { name: 'Payment session', url: '' }
  }
}

export const detectedProgressLabel = (row: DetectedRow): string => {
  if (row.awaitsFinalizedPass) {
    return 'Waiting for finality'
  }
  if (row.blockNumber === undefined || row.blockNumber === null) {
    return 'Seen, not yet in a block'
  }
  const seen = Math.min(Math.max(row.confirmations, 0), row.requiredConfirmations)
  return `${seen} of ${row.requiredConfirmations} confirmations`
}

export const detectedStatusLabel = 'Awaiting confirmations'

const fromDeposit = (d: DetectedDeposit, accountName?: string): DetectedRow => ({
  id: d.id,
  kind: 'deposit',
  network: d.network,
  token: d.token,
  transactionHash: d.transactionHash,
  amount: d.amount,
  blockNumber: d.blockNumber,
  seenAt: d.blockTimestamp,
  confirmations: d.confirmations,
  requiredConfirmations: d.requiredConfirmations,
  awaitsFinalizedPass: d.awaitsFinalizedPass,
  senderAddresses: d.senderAddresses ?? [],
  related: {
    name: accountName || shortAddress(d.depositAccountId),
    url: '/deposit_addresses',
    subtitle: 'Deposit address'
  }
})

const fromPayment = (p: DetectedPayment): DetectedRow => {
  const source = sourceLabel(p.associatedEntityType)
  return {
    id: p.id,
    kind: 'payment',
    network: p.network,
    token: p.token,
    transactionHash: p.transactionHash,
    amount: p.amount,
    expectedAmount: p.expectedAmount,
    blockNumber: p.blockNumber,
    seenAt: p.blockTimestamp ?? p.startedAt,
    confirmations: p.confirmations,
    requiredConfirmations: p.requiredConfirmations,
    awaitsFinalizedPass: p.awaitsFinalizedPass,
    senderAddresses: p.senderAddresses ?? [],
    related: { name: source.name, url: source.url, subtitle: shortAddress(p.associatedEntityId, 6) }
  }
}

/**
 * Detected deposits and payments for the organization, refreshed every 30
 * seconds while mounted. Either source failing leaves the other in place.
 */
export function useDetectedTransactions (isTestMode: boolean): { rows: DetectedRow[] } {
  const [rows, setRows] = useState<DetectedRow[]>([])
  const accountNames = useRef<Record<string, string>>({})

  useEffect(() => {
    let cancelled = false
    let timer: ReturnType<typeof setTimeout> | undefined

    const load = async (): Promise<void> => {
      const [deposits, payments] = await Promise.all([
        Radom.listDetectedDeposits(isTestMode).catch(() => [] as DetectedDeposit[]),
        Radom.listDetectedPayments(isTestMode).catch(() => [] as DetectedPayment[])
      ])
      if (cancelled) return

      const unknown = Array.from(new Set(deposits.map(d => d.depositAccountId)))
        .filter(id => !(id in accountNames.current))
      await Promise.all(unknown.map(async id => {
        try {
          const account = await Radom.getDepositAccount(id)
          accountNames.current = { ...accountNames.current, [id]: account.name }
        } catch {
          accountNames.current = { ...accountNames.current, [id]: '' }
        }
      }))
      if (cancelled) return

      const next = [
        ...deposits.map(d => fromDeposit(d, accountNames.current[d.depositAccountId])),
        ...payments.map(fromPayment)
      ].sort((a, b) => new Date(b.seenAt).getTime() - new Date(a.seenAt).getTime())
      setRows(next)
    }

    const tick = async (): Promise<void> => {
      try {
        await load()
      } finally {
        if (!cancelled) timer = setTimeout(tick, REFRESH_MS)
      }
    }

    setRows([])
    tick()

    return () => {
      cancelled = true
      if (timer) clearTimeout(timer)
    }
  }, [isTestMode])

  return { rows }
}

const Hero = styled(DrawerCard)`
  gap: 14px;
`

const HeroTop = styled.div`
  align-items: flex-start;
  display: flex;
  gap: 12px;
  justify-content: space-between;
`

const HeroIdentity = styled.div`
  align-items: center;
  display: flex;
  gap: 12px;
  min-width: 0;
`

const HeroTitle = styled.span`
  color: var(--ink, #20242d);
  display: block;
  font-size: 15px;
  font-weight: 500;
`

const HeroMeta = styled.span`
  color: var(--text-muted, #768092);
  display: block;
  font-size: 12px;
`

const HeroAmount = styled.div`
  align-items: baseline;
  display: flex;
  gap: 8px;
`

const HeroAmountValue = styled.span`
  color: ${RADOM_COLORS.NICE_GREEN};
  font-size: 28px;
  font-weight: 500;
  letter-spacing: -0.01em;
`

const HeroTicker = styled.span`
  color: var(--text-muted, #768092);
  font-size: 14px;
`

const DrawerNote = styled(InlineNotice)`
  display: block;
  font-size: 12.5px;
`

const Mono = styled.span`
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 12px;
`

const ExplorerLink = styled.a`
  align-items: center;
  color: inherit;
  display: inline-flex;
  gap: 4px;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`

const networkLabel = (network: string): string => {
  const method = getMethod(network, undefined)
  return method.hostChain?.name ?? method.name ?? network
}

/** Side panel for one detected transaction, in the style of the transaction drawer. */
export const DetectedTransactionDrawer = (props: { row: DetectedRow, onClose: () => void }): ReactElement => {
  const { row, onClose } = props
  const method = getMethod(row.network, row.token)
  const title = row.kind === 'deposit' ? 'Detected deposit' : 'Detected payment'
  return <DetailsDrawer
    visible
    onClose={onClose}
    eyebrow={detectedStatusLabel}
    title={`+ ${roundAssetAmount(method.ticker, row.amount)} ${method.ticker}`}
    subtitle={row.related.name}
    width={520}
    ariaLabel={`${title} details`}
  >
    <Hero>
      <HeroTop>
        <HeroIdentity>
          <PaymentMethodDisplay
            method={{ network: row.network, token: row.token }}
            excludeName
            disableTooltip
            size={36} />
          <div style={{ minWidth: 0 }}>
            <HeroTitle>{title}</HeroTitle>
            <HeroMeta>
              Seen {formatDistance(new Date(row.seenAt), new Date(), { addSuffix: true })}
            </HeroMeta>
          </div>
        </HeroIdentity>
        <InlinePill $tone='amber'>{detectedProgressLabel(row)}</InlinePill>
      </HeroTop>
      <HeroAmount>
        <HeroAmountValue>+ {roundAssetAmount(method.ticker, row.amount)}</HeroAmountValue>
        <HeroTicker>{method.ticker}</HeroTicker>
      </HeroAmount>
    </Hero>
    <DrawerCard>
      <DrawerDetailRow>
        <DrawerDetailLabel>Credited</DrawerDetailLabel>
        <DrawerDetailValue>Not yet</DrawerDetailValue>
      </DrawerDetailRow>
      {row.expectedAmount !== undefined && <DrawerDetailRow>
        <DrawerDetailLabel>Amount due</DrawerDetailLabel>
        <DrawerDetailValue>{roundAssetAmount(method.ticker, row.expectedAmount)} {method.ticker}</DrawerDetailValue>
      </DrawerDetailRow>}
      <DrawerDetailRow>
        <DrawerDetailLabel>Network</DrawerDetailLabel>
        <DrawerDetailValue>{networkLabel(row.network)}</DrawerDetailValue>
      </DrawerDetailRow>
      <DrawerDetailRow>
        <DrawerDetailLabel>Asset</DrawerDetailLabel>
        <DrawerDetailValue>{method.name} ({method.ticker})</DrawerDetailValue>
      </DrawerDetailRow>
      <DrawerDetailRow>
        <DrawerDetailLabel>{row.kind === 'deposit' ? 'Deposit account' : 'Source'}</DrawerDetailLabel>
        <DrawerDetailValue>
          {row.related.url
            ? <Link to={row.related.url} style={{ color: 'inherit' }}>{row.related.name}</Link>
            : row.related.name}
          {row.kind === 'payment' && <Mono style={{ marginLeft: 6, opacity: 0.7 }}>{row.related.subtitle}</Mono>}
        </DrawerDetailValue>
      </DrawerDetailRow>
      <DrawerDetailRow>
        <DrawerDetailLabel>Seen on chain</DrawerDetailLabel>
        <DrawerDetailValue>{new Date(row.seenAt).toLocaleString()}</DrawerDetailValue>
      </DrawerDetailRow>
      <DrawerDetailRow>
        <DrawerDetailLabel>Block</DrawerDetailLabel>
        <DrawerDetailValue>
          {row.blockNumber !== undefined && row.blockNumber !== null
            ? <Mono>{row.blockNumber.toLocaleString()}</Mono>
            : 'Not yet in a block'}
        </DrawerDetailValue>
      </DrawerDetailRow>
      <DrawerDetailRow>
        <DrawerDetailLabel>Transaction</DrawerDetailLabel>
        <DrawerDetailValue>
          <ExplorerLink
            href={formatExplorerUrl(row.network, 'tx', row.transactionHash)}
            target='_blank'
            rel='noreferrer'>
            <Mono>{shortAddress(row.transactionHash, 10)}</Mono>
            <LinkExternal width={12} height={12} />
          </ExplorerLink>
        </DrawerDetailValue>
      </DrawerDetailRow>
      {row.senderAddresses.map((sender, i) => <DrawerDetailRow key={`${sender.address}-${i}`}>
        <DrawerDetailLabel>{i === 0 ? 'From' : ''}</DrawerDetailLabel>
        <DrawerDetailValue>
          <ExplorerLink
            href={formatExplorerUrl(row.network, 'address', sender.address)}
            target='_blank'
            rel='noreferrer'>
            <Mono>{shortAddress(sender.address, 8)}</Mono>
            <LinkExternal width={12} height={12} />
          </ExplorerLink>
        </DrawerDetailValue>
      </DrawerDetailRow>)}
    </DrawerCard>
    <DrawerNote>
      Nothing has been credited for this transaction yet. Once the network confirms it, it becomes a
      {row.kind === 'deposit' ? ' deposit' : ' completed payment'} and your balance updates. If the network drops it,
      it leaves the list and nothing is credited.
    </DrawerNote>
  </DetailsDrawer>
}
